// Screen · My plans (route /plans) — every lesson plan the signed-in user wrote this term, newest
// week first, for all their classes. Before this, a plan from an earlier week could only be found
// through a class page. Coordination facts only (topic · week · status · taught) — the plan itself
// opens on its own page. ?show=draft|submitted|approved filters; ?class=<id> narrows to one class.

import { api } from "../api";
import { navigate } from "../router";
import { esc } from "../dom";
import { t } from "../i18n";
import { icon } from "../icons";
import { byGrade, wireDraftDelete, type ClassRow } from "../lesson-data";
import { pageHeader, statusChip, emptyState, linkTabs, toast } from "../ui";

interface Row {
  lesson_id: string;
  plan_id: string | null;
  week_no: number;
  topic: string;
  status: "not_started" | "in_progress" | "submitted" | "approved";
  node_id: string | null;
  teaching_state?: "not_recorded" | "taught" | "confirmation_withdrawn";
}
interface ClassLessons { term: { id: string; name: string } | null; lessons: Row[] }

const SHOW: Record<string, { label: string; match: (r: Row) => boolean }> = {
  all: { label: "All", match: () => true },
  draft: { label: "Drafts", match: (r) => isDraft(r) },
  submitted: { label: "Submitted", match: (r) => r.status === "submitted" },
  approved: { label: "Approved", match: (r) => r.status === "approved" },
};

const isDraft = (r: Row): boolean => r.status === "in_progress" || r.status === "not_started";

function hrefFor(r: Row): string {
  if (isDraft(r)) return `/lesson/${encodeURIComponent(r.lesson_id)}/edit`;
  return r.plan_id ? `/plan/${encodeURIComponent(r.plan_id)}` : `/lesson/${encodeURIComponent(r.lesson_id)}`;
}

export async function renderPlans(page: HTMLElement, _params: Record<string, string>, query: URLSearchParams): Promise<void> {
  // Only SHOW's own keys (a URL like ?show=constructor must not reach Object.prototype).
  const asked = query.get("show") ?? "all";
  const show = Object.prototype.hasOwnProperty.call(SHOW, asked) ? asked : "all";
  const onlyClass = query.get("class");
  const { classes: raw } = await api<{ classes: ClassRow[] }>("/api/classes?scope=own");
  const classes = [...raw].sort(byGrade);
  const lists = await Promise.all(classes.map(async (c) => {
    try {
      return { c, data: await api<ClassLessons>(`/api/classes/${encodeURIComponent(c.id)}/lessons`) };
    } catch {
      return { c, data: { term: null, lessons: [] } as ClassLessons };
    }
  }));
  const term = lists.find((l) => l.data.term)?.data.term ?? null;
  const all = lists.flatMap(({ c, data }) => data.lessons.map((r) => ({ c, r })));
  const filtered = all.filter(({ c, r }) => (!onlyClass || c.id === onlyClass) && SHOW[show]!.match(r));

  const qs = (over: { show?: string; cls?: string | null }): string => {
    const p = new URLSearchParams();
    const s = over.show ?? show;
    const c = over.cls !== undefined ? over.cls : onlyClass;
    if (s !== "all") p.set("show", s);
    if (c) p.set("class", c);
    const str = p.toString();
    return `/plans${str ? `?${str}` : ""}`;
  };
  const tabs = linkTabs(Object.entries(SHOW).map(([k, v]) => ({
    label: v.label, href: qs({ show: k }), on: k === show,
    count: all.filter(({ c, r }) => (!onlyClass || c.id === onlyClass) && v.match(r)).length,
  })), "Filter plans");
  const classPick = classes.length > 1
    ? `<label class="inline-field"><span class="sr-only">Class</span><select id="plans-class" aria-label="Class">
        <option value="">All classes</option>${classes.map((c) => `<option value="${esc(c.id)}"${c.id === onlyClass ? " selected" : ""}>${esc(c.name)}</option>`).join("")}
      </select></label>`
    : "";

  const weeks = [...new Set(filtered.map(({ r }) => r.week_no))].sort((a, b) => b - a);
  // A draft row carries a small Delete beside its link (owner ruling A1, 21 Oct 2026): two taps, and the server says in a
  // plain sentence when it keeps a draft (a saved worksheet; a plan the Lead sent back). A control cannot sit inside a link,
  // so a draft's row is the same row laid out with the link and the button side by side.
  const rowHtml = ({ c, r }: { c: ClassRow; r: Row }): string => isDraft(r)
    ? `<div class="row-link static" data-lesson="${esc(r.lesson_id)}" data-draft-row>
            <a class="rl-main" data-link href="${esc(hrefFor(r))}" style="color:inherit;text-decoration:none"><span class="class-badge">${esc(c.name)}</span> ${esc(r.topic)}</a>
            <span class="rl-meta">${statusChip(r.status)}</span>
            <button type="button" class="btn secondary sm" data-delete-draft="${esc(r.lesson_id)}" aria-label="Delete the draft ${esc(r.topic)}">Delete</button>
            <span class="error" data-row-err role="alert" style="flex-basis:100%" hidden></span>
          </div>`
    : `<a class="row-link" data-link href="${esc(hrefFor(r))}" data-lesson="${esc(r.lesson_id)}">
            <span class="class-badge">${esc(c.name)}</span>
            <span class="rl-main">${esc(r.topic)}</span>
            <span class="rl-meta">${statusChip(r.status)}${r.teaching_state === "taught" ? ` <span class="st st-taught chip taught">${icon("check")}Taught</span>` : ""}</span>
            ${icon("chevron", "ico-sm")}
          </a>`;
  const body = filtered.length
    ? weeks.map((w) => `<section class="plan-week"><h2 class="sec-title">Week ${w}</h2><div class="list">${filtered
        .filter(({ r }) => r.week_no === w)
        .map(rowHtml).join("")}</div></section>`).join("")
    : classes.length
      ? emptyState(show === "all" ? "No plans yet this term." : `No ${SHOW[show]!.label.toLowerCase()} plans.`,
          `<a class="btn" data-link href="/plan/start">${icon("pencil")}<span>${t("week.plan_button")}</span></a>`)
      : emptyState("You are not in a class yet. Your Lead adds classes in Setup — or you can add one yourself.",
          `<a class="btn secondary" data-link href="/classes">${icon("plus")}<span>Add a class</span></a>`);

  page.innerHTML = `
    ${pageHeader({
      eyebrow: term?.name ?? "",
      title: t("nav.my_plans"),
      sub: "Your lesson plans this term, newest week first.",
      // The header's "Plan a lesson" only when the list has plans — an empty list carries its own.
      actions: `${classPick}${filtered.length ? `<a class="btn" data-link href="/plan/start">${icon("plus")}<span>${t("week.plan_button")}</span></a>` : ""}`,
    })}
    ${tabs}
    ${body}`;

  // A refusal is written in the row that was tapped, under its Delete button — not at the top of a long list.
  page.querySelectorAll<HTMLButtonElement>("[data-delete-draft]").forEach((button) => {
    const rowErr = button.closest("[data-draft-row]")!.querySelector<HTMLElement>("[data-row-err]")!;
    wireDraftDelete(button, button.dataset.deleteDraft!, {
      ask: "Delete? Tap again",
      done: () => { toast("Draft deleted"); window.dispatchEvent(new Event("eden:rerender")); },
      fail: (message) => { rowErr.textContent = message; rowErr.hidden = false; },
    });
  });
  (page.querySelector("#plans-class") as HTMLSelectElement | null)?.addEventListener("change", (e) => {
    const v = (e.target as HTMLSelectElement).value || null;
    navigate(qs({ cls: v }));
  });
}
